import React, { useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";

import { SimulationContext, SimulationContextProps } from "src/pages/coaching/components/simulation/view/context";

import { fetchPublicSimulation } from "src/redux/actions/coaching/simulation";
import { completeSimulation, enrollSimulation, fetchLearningRecordSimulation, fetchSimulationReport, fetchTranscriptsForLearningSimulation, invalidateCompleteSimulation, invalidateSimulationReport, restartSimulation, retakeSimulation, startSimulation } from "src/redux/actions/coaching/learners";

import { SimulationSummaryComponent } from "src/pages/coaching/components/simulation/view";
import EmptyMessage from "src/components/emptyMessage";

import { LearningRecordSimulation } from "src/types/coaching/learners";
import { LearningRecordCourse } from "src/types/coaching/learners";
import { SimulationContent } from "src/types/coaching/courses";

import _ from "lodash";
import styles from "./simulation.module.scss";

interface ViewSimulationContentProps {
  course: LearningRecordCourse;
  content: SimulationContent;
  onSimulatorModeChange?: (isSimulator: boolean) => void;
}

export const ViewSimulationContent = ({
  course,
  content,
  onSimulatorModeChange,
}: ViewSimulationContentProps) => {

  const dispatch = useDispatch();

  const id = _.get(content, "content.id");

  const publicSimulation = useSelector((state: any) =>
    _.get(state.coaching, `simulations.simulation[${id}].item`)
  );

  const loadingPublicSimulation = useSelector((state: any) =>
    _.get(state.coaching, `simulations.simulation[${id}].loading`)
  );

  const enrollingSimulation = useSelector((state: any) =>
    _.get(state.coaching, `simulations.simulation[${id}].enroll.enrolling`)
  );

  const simulationEnrolledId = useSelector((state: any) =>
    _.get(state.coaching, `simulations.simulation[${id}].enroll.id`)
  );

  const learnerSimulation = useSelector((state: any) =>
    _.get(state.learners, `simulation[${simulationEnrolledId}].item`)
  );

  const loadingLearnerSimulation = useSelector((state: any) =>
    _.get(state.learners, `simulation[${simulationEnrolledId}].loading`)
  );

  const simulationAssessmentReport = useSelector((state: any) => {
    return _.get(state.learners, `simulation[${simulationEnrolledId}].item.report`);
  });

  const startingSimulation = useSelector((state: any) =>
    _.get(state.learners, `simulation[${simulationEnrolledId}].start.starting`)
  );

  const completingSimulation = useSelector((state: any) => {
    return _.get(state.learners, `simulation[${simulationEnrolledId}].complete.completing`);
  });

  const simulationCompleted = useSelector((state: any) => {
    return _.get(state.learners, `simulation[${simulationEnrolledId}].complete.completed`);
  });

  const fetchingSimulationReport = useSelector((state: any) => {
    const fetching = _.get(state.learners, `simulation[${simulationEnrolledId}].report.fetching`);
    return fetching;
  });

  const fetchedSimulationReport = useSelector((state: any) => {
    const fetched = _.get(state.learners, `simulation[${simulationEnrolledId}].report.fetched`);
    return fetched;
  });

  const transcripts = useSelector((state: any) => {
    return _.get(state.learners, `simulation[${simulationEnrolledId}].transcripts.item`);
  });

  const fetchingSimulationTranscripts = useSelector((state: any) => {
    const fetching = _.get(state.learners, `simulation[${simulationEnrolledId}].transcripts.fetching`);
    return fetching;
  });

  const fetchedSimulationTranscripts = useSelector((state: any) => {
    const fetched = _.get(state.learners, `simulation[${simulationEnrolledId}].transcripts.fetched`);
    return fetched;
  });

  const simulationStarted = useSelector((state: any) =>
    _.get(state.learners, `simulation[${simulationEnrolledId}].start.started`)
  );

  const restartingSimulation = useSelector((state: any) =>
    _.get(state.learners, `simulation[${simulationEnrolledId}].restart.restarting`)
  );

  const simulationRestarted = useSelector((state: any) =>
    _.get(state.learners, `simulation[${simulationEnrolledId}].restart.restarted`)
  );

  const retakingSimulation = useSelector((state: any) =>
    _.get(state.learners, `simulation[${simulationEnrolledId}].retake.retaking`)
  );

  const [simulationContext, setSimulationContext] =
    React.useState<SimulationContextProps>({
      id,
      view: "summary",
    });

  const handleChangeView = (view: any) => {
    setSimulationContext((prev) => {
      return {
        ...prev,
        view: view,
        secondaryView: null,
      }
    });
  }

  const handleSecondaryChangeView = (view: any) => {
    setSimulationContext((prev) => {
      return {
        ...prev,
        secondaryView: view,
      }
    });
  }

  const handleEnrollSimulation = React.useCallback(() => {

    if (!publicSimulation) {
      return;
    }

    dispatch(
      enrollSimulation({
        course: publicSimulation.id!,
        parentCourse: course.id!,
      })
    );
  }, [publicSimulation]);

  const handleStartSimulation = () => {
    if (simulationEnrolledId) {
      dispatch(startSimulation(simulationEnrolledId));
    }
  };

  const handleRestartSimulation = () => {
    if (simulationEnrolledId) {
      dispatch(restartSimulation(simulationEnrolledId));
    }
  };

  const handleRetakeSimulation = () => {
    if (simulationEnrolledId) {
      dispatch(retakeSimulation(simulationEnrolledId));
    }
  };

  const handleEndCourse = (simulation: LearningRecordSimulation) => {
    dispatch(completeSimulation({
      id: simulation.id!,
      session: simulation.session!,
    }));
  };

  const handleViewReport = () => {
    if (!simulationAssessmentReport) {
      dispatch(fetchSimulationReport({
        id: learnerSimulation.id!,
      }));
    } else {
      handleChangeView("report");
    }
  }

  const handleFetchTranscripts = () => {
    dispatch(fetchTranscriptsForLearningSimulation(learnerSimulation.id!));
  }

  useEffect(() => {
    if (id && !publicSimulation) {
      dispatch(fetchPublicSimulation({ id }));
    }

    setSimulationContext((prev) => ({
      ...prev,
      id,
      view: "summary",
    }));
  }, []);

  useEffect(() => {
    if (!simulationEnrolledId) return;

    dispatch(fetchLearningRecordSimulation(simulationEnrolledId));
  }, [simulationEnrolledId]);

  useEffect(() => {
    setSimulationContext((prev) => ({
      ...prev,
      view: learnerSimulation?.status === 'IN_PROGRESS' ? "simulator" : "summary",
      enrollingSimulation,
      startingSimulation,
      restartingSimulation,
      retakingSimulation,
      enrollSimulation: handleEnrollSimulation,
      startSimulation: handleStartSimulation,
      restartSimulation: handleRestartSimulation,
      retakeSimulation: handleRetakeSimulation,
      secondaryView: null,
      endSimulation: handleEndCourse,
      viewReport: handleViewReport,
      fetchTranscripts: handleFetchTranscripts,
      changeView: handleChangeView,
      changeSecondaryView: handleSecondaryChangeView,
    }));
  }, [learnerSimulation,
    publicSimulation,
    enrollingSimulation,
    startingSimulation,
    restartingSimulation,
    retakingSimulation]);

  useEffect(() => {
    if (simulationStarted && learnerSimulation?.status === "IN_PROGRESS") {
      setSimulationContext((prev) => ({
        ...prev,
        view: "simulator",
      }));
    }
  }, [simulationStarted, learnerSimulation?.status]);


  useEffect(() => {
    if (simulationRestarted) {
      setSimulationContext((prev) => ({
        ...prev,
        view: "simulator",
      }));
    }
  }, [simulationRestarted]);

  useEffect(() => {
    setSimulationContext((prev) => {
      return {
        ...prev,
        endingSimulation: completingSimulation
      }
    });
  }, [completingSimulation]);

  useEffect(() => {
    if (simulationCompleted) {
      const status = _.get(learnerSimulation, "report.status");
      if (status === "in_sufficient_duration") {
        handleChangeView("failed");
      } else {
        handleChangeView("completion");
      }
      dispatch(invalidateCompleteSimulation(learnerSimulation.id));
    }
  }, [simulationCompleted]);

  useEffect(() => {
    setSimulationContext((prev) => {
      return {
        ...prev,
        fetchingReport: fetchingSimulationReport,
        fetchedReport: fetchedSimulationReport,
        assessmentReport: simulationAssessmentReport,
      }
    });

    if (fetchedSimulationReport) {
      handleChangeView("report");
    }

    if (learnerSimulation) {
      dispatch(invalidateSimulationReport(learnerSimulation.id));
    }

  }, [fetchingSimulationReport, fetchedSimulationReport, simulationAssessmentReport]);

  useEffect(() => {
    setSimulationContext((prev) => {
      return {
        ...prev,
        fetchingTranscripts: fetchingSimulationTranscripts,
        fetchedTranscripts: fetchedSimulationTranscripts,
        transcripts: transcripts
      }
    });
  }, [fetchingSimulationTranscripts, fetchedSimulationTranscripts, transcripts]);

  useEffect(() => {
    setSimulationContext((prev) => {
      return {
        ...prev,
        retakingSimulation: retakingSimulation,
      }
    });
  }, [retakingSimulation]);


  useEffect(() => {
    const fullScreenViews = ["simulator", "report"];
    const isFullScreen = fullScreenViews.includes(simulationContext.view);
  
    onSimulatorModeChange?.(isFullScreen);
  }, [simulationContext.view]);

  let simulation: LearningRecordSimulation | null = null;

  if (simulationEnrolledId) {
    simulation = learnerSimulation;
  } else if (publicSimulation) {
    simulation = {
      id: publicSimulation.id!,
      simulation: publicSimulation,
      session: undefined,
      status: "NOT_ENROLLED",
    };
  }
  
  const loading = loadingPublicSimulation || loadingLearnerSimulation;

  if (!loading && !simulation) {
    return (
      <div className={styles.notFoundContainer}>
        <EmptyMessage
          width={500}
          image="/images/assets/404error_message.svg"
          heading="Mmm... who moved my cheese?"
          message="Sorry! Someone passed you the wrong information. You were never supposed to see me :). This podcast does not exist any longer."
        />
      </div>
    );
  }

  if (loading || !simulation) {
    return (
      <div className={styles.loading}>
        <img src="/images/loading_plane.gif" />
        <div className={styles.message}>Loading simulation...</div>
      </div>
    );
  }

  return (
    <SimulationContext.Provider value={simulationContext}>
      <SimulationSummaryComponent
        simulation={simulation}
        className={styles.simulationView}
        isReadOnly
      />
    </SimulationContext.Provider>
  );
};
